import { jsxs, jsx } from 'react/jsx-runtime';
import { IconSubDocSearch30SubDocSearch, IconNavigational24Cross, IconNavigational24DownChevron, IconNavigational24UpChevron } from '@fabric-app/icons/svg';
import { ButtonIcon, Button } from '@fabric-app/ui/components/Button';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { styled } from 'styled-components';
import { useSubDocumentSearch } from '../../context/SubDocumentSearchContext.js';
import { MultiSelect } from '@fabric-app/ui/components/MultiSelect/MultiSelect';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { isTruthy } from '@fabric-app/utils/guards';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { isHotkey } from 'is-hotkey';
import { P } from '@fabric-app/ui/components/Typography';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { useRef } from 'react';
import { useWindowEvent } from '@fabric-app/hooks/useWindowEvent';

const SubDocumentSearchContainer = styled(Flex).attrs((p) => {
  return {
    gap: "text",
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "SubDocumentSearch__SubDocumentSearchContainer",
  componentId: "sc-63e338bc-0"
})([
  `display:flex;padding:0.375rem;position:absolute;top:`,
  `px;right:0;border:1px solid `,
  `;border-top-color:transparent;border-right-color:transparent;border-radius:0 0 0 0.25rem;background:`,
  `;z-index:151;`,
  `{width:100%;border-radius:0;padding-top:calc( 0.375rem + max(var(--safe-margin-offset-top),env(safe-area-inset-top),10px) );border:none;border-bottom:1px solid `,
  `;}`
], (p) => p.offsetY ?? 0, cssVar["color-border-primary"], cssVar["color-bg-primary"], mediaMobile, cssVar["color-border-primary"]);
const SubDocumentInputField = styled(Flex).attrs((p) => {
  return {
    gap: "element",
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "SubDocumentSearch__SubDocumentInputField",
  componentId: "sc-63e338bc-1"
})([
  `padding:0.625rem 0.375rem;background:`,
  `;height:36px;border-radius:0.625rem;width:330px;`,
  `{width:auto;flex-grow:1;min-width:0;}`
], cssVar["color-bg-tertiary"], mediaMobile);
const SubDocumentInput = styled.input.withConfig({
  displayName: "SubDocumentSearch__SubDocumentInput",
  componentId: "sc-63e338bc-2"
})([
  `background:transparent;border:none;outline:none;font-size:1rem;font-weight:500;color:`,
  `;flex-grow:1;min-width:0;`
], cssVar["color-text-primary"]);
const SubDocumentSearchNavButton = styled(ButtonIcon).attrs((p) => {
  return {
    size: "default",
    variant: "transparent",
    ...p
  };
}).withConfig({
  displayName: "SubDocumentSearch__SubDocumentSearchNavButton",
  componentId: "sc-63e338bc-3"
})([
  ``
]);
const SubDocumentInputClearButton = styled(ButtonIcon).attrs((p) => {
  return {
    ...p,
    size: "xss",
    variant: "bg-senary"
  };
}).withConfig({
  displayName: "SubDocumentSearch__SubDocumentInputClearButton",
  componentId: "sc-63e338bc-4"
})([
  `color:white;`
]);
const SubDocumentDropdownButton = styled(Button).attrs((p) => {
  return {
    ...p,
    size: "xss",
    variant: "transparent",
    as: DropdownMenu.Trigger
  };
}).withConfig({
  displayName: "SubDocumentSearch__SubDocumentDropdownButton",
  componentId: "sc-63e338bc-5"
})([
  `color:`,
  `;padding:0;`
], cssVar["color-text-primary"]);
const SubDocumentInputMatchCount = styled(P).attrs((p) => {
  return {
    ...p,
    size: "lg",
    weight: 500
  };
}).withConfig({
  displayName: "SubDocumentSearch__SubDocumentInputMatchCount",
  componentId: "sc-63e338bc-6"
})([
  `white-space:nowrap;`
]);
const SubDocumentMobileDoneButton = styled(Button).attrs((p) => {
  return {
    ...p,
    size: "mobile-default",
    variant: "transparent"
  };
}).withConfig({
  displayName: "SubDocumentSearch__SubDocumentMobileDoneButton",
  componentId: "sc-63e338bc-7"
})([
  `display:none;`,
  `{display:flex;}font-size:1rem;color:`,
  `;font-weight:500;`
], mediaMobile, cssVar["color-text-primary"]);
const SubDocumentSearch = ({ offsetY }) => {
  const inputRef = useRef(null);
  const { setSearchText, matchCase, setMatchCase, wholeWords, setWholeWords, currentMatch, totalMatches, goToMatch, isOpen, isAvailable, setOpen } = useSubDocumentSearch();
  const multiselectValue = [
    matchCase ? "matchCase" : void 0,
    wholeWords ? "wholeWords" : void 0
  ].filter(isTruthy);
  const onSelectItem = (selectedItem) => {
    if (selectedItem === "matchCase") {
      setMatchCase(!matchCase);
    } else if (selectedItem === "wholeWords") {
      setWholeWords(!wholeWords);
    }
  };
  const goToNextMatch = () => {
    const nextMatch = currentMatch + 1 >= totalMatches ? 0 : currentMatch + 1;
    goToMatch(nextMatch);
  };
  const goToPreviousMatch = () => {
    const previousMatch = currentMatch - 1 < 0 ? totalMatches - 1 : currentMatch - 1;
    goToMatch(previousMatch);
  };
  const { value, onChange } = useInputControls("", {
    debounce: 100,
    onChange(value2) {
      setSearchText(value2);
    }
  });
  const onKeyDown = (event) => {
    if (!isHotkey([
      "enter",
      "shift+enter"
    ], event)) return;
    if (event.shiftKey) {
      goToPreviousMatch();
    } else {
      goToNextMatch();
    }
  };
  const onDone = () => {
    setOpen(false);
  };
  useWindowEvent("keydown", (event) => {
    if (!isHotkey("mod+f", event)) return;
    if (!isOpen) {
      setOpen(true);
      event.preventDefault();
      return;
    }
    if (document.activeElement === inputRef.current) return;
    inputRef.current?.focus();
    event.preventDefault();
  });
  const onClear = () => {
    onChange("");
  };
  if (!isOpen || !isAvailable) return null;
  return /* @__PURE__ */ jsxs(SubDocumentSearchContainer, {
    offsetY,
    children: [
      /* @__PURE__ */ jsx(SubDocumentMobileDoneButton, {
        onClick: onDone,
        children: "Done"
      }),
      /* @__PURE__ */ jsxs(SubDocumentInputField, {
        children: [
          /* @__PURE__ */ jsx(MultiSelect, {
            trigger: {
              render: (props) => /* @__PURE__ */ jsx(SubDocumentDropdownButton, {
                ...props,
                children: /* @__PURE__ */ jsx(IconSubDocSearch30SubDocSearch, {})
              })
            },
            options: [
              {
                label: "Match case",
                value: "matchCase",
                stringLabel: "Match case"
              },
              {
                label: "Whole words",
                value: "wholeWords",
                stringLabel: "Whole words"
              }
            ],
            value: multiselectValue,
            onSelectItem,
            title: "Search"
          }),
          /* @__PURE__ */ jsx(SubDocumentInput, {
            ref: inputRef,
            placeholder: "Find",
            value,
            onChange,
            onKeyDown,
            autoFocus: true
          }),
          value && totalMatches > 0 && /* @__PURE__ */ jsxs(SubDocumentInputMatchCount, {
            children: [
              currentMatch + 1,
              " of ",
              totalMatches
            ]
          }),
          value && /* @__PURE__ */ jsx(SubDocumentInputClearButton, {
            onClick: onClear,
            children: /* @__PURE__ */ jsx(IconNavigational24Cross, {})
          })
        ]
      }),
      /* @__PURE__ */ jsx(SubDocumentSearchNavButton, {
        onClick: goToNextMatch,
        children: /* @__PURE__ */ jsx(IconNavigational24DownChevron, {})
      }),
      /* @__PURE__ */ jsx(SubDocumentSearchNavButton, {
        onClick: goToPreviousMatch,
        children: /* @__PURE__ */ jsx(IconNavigational24UpChevron, {})
      })
    ]
  });
};

export { SubDocumentSearch as default };
//# sourceMappingURL=SubDocumentSearch.js.map
